// chart Types
import { aggregatorType, dateScopeDynamicType, dateScopeType, routeName } from 'shared/constants.json';

// completelyLoaded markers
export const COMPLETELY_LOADED_DEFAULT = 'default';
export const PLANNING_LOADED_MARKER = 'planning';

export const AREASPLINE = 'areaspline';
export const LINE = 'line';
export const AREA = 'area';

export const PROGRESS_OVER_TIME_AGGREGATORS = [
  aggregatorType.progressOverTime,
  aggregatorType.progressOverTimeUnbounded,
];

export const TIME_SERIES_AGGREGATORS = [
  ...PROGRESS_OVER_TIME_AGGREGATORS,
  aggregatorType.statusScoreOverTime,
  aggregatorType.statusDistributionOverTime,
];

// `count` is a snapshot aggregator too, but it stands apart here: it is the one
// that counts entities instead of aggregating a chosen edge or property.
export const SNAPSHOT_AGGREGATORS = [
  aggregatorType.sum,
  aggregatorType.avg,
  aggregatorType.min,
  aggregatorType.max,
  aggregatorType.countDistinct,
];

// highcharts
export const DASH = 'Dash';
export const DOT = 'Dot';
export const DATETIME = 'datetime';

// Goal Cycles
export const UNASSIGNED = 'unassigned';

// compare to previous week
export const ONE_DAY_AGO = 'one_day_ago';
export const ONE_WEEK_AGO = 'previous_week';
export const ONE_MONTH_AGO = 'previous_month';
export const NONE = 'none';
export const CUSTOM_DATE = 'custom_date';

export const LT = dateScopeType.lessThan;
export const GT = dateScopeType.greaterThan;
export const BETWEEN = dateScopeType.between;
export const EMPTY = dateScopeType.empty;
export const NOT_EMPTY = dateScopeType.notEmpty;

export const EXACT_DATE = dateScopeDynamicType.exactDate;
export const TODAY = dateScopeDynamicType.today;
export const LAST_X_DAYS = dateScopeDynamicType.lastXDays;
export const NEXT_X_DAYS = dateScopeDynamicType.nextXDays;
export const THIS_WEEK = dateScopeDynamicType.thisWeek;
export const LAST_WEEK = dateScopeDynamicType.lastWeek;
export const THIS_MONTH = dateScopeDynamicType.thisMonth;
export const LAST_MONTH = dateScopeDynamicType.lastMonth;

export const FILE_UPLOAD_LIMIT = 20000000;

export const GOAL_EXPORT_LIMIT = 10;

export const GOAL_PARENT_QUERY_LEVEL = 10;

// Sorting
export const MANUAL_SORTING = 'manual_sorting';

// Cascade
export const CASCADE_CONTEXT_PLANNING = 'planning';
export const CASCADE_CONTEXT_PUBLISHED_VIEW = 'published_view';

// Errors
export const ERRORS = {
  SORT: {
    PARENT_NOT_EDITABLE: 'parent_not_editable',
    VIEW_NOT_EDITABLE: 'view_not_editable',
  },
  REALIGN: { MISSING_ACCESS_RIGHTS_FOR_GOAL: 'missing_access_rights_goal' },
};

// services
export const VIEWS_SERVICE = 'viewsService';
export const VIEW_EVENT_BUS = 'viewEventBus';

export const GOAL_CHILDREN_LIST_VIEW_APPLICATION = 'goal_children_list';

export const SIDEBAR_SERVICE = 'sidebarService';
export const APP_RESTART = 'appRestart';
export const GOALS_SHOULD_EXCLUDE = 'goalsShouldExclude';
export const GOALS_LOADED_MARKER = 'goalsLoadedMarker';

// static property keys
export const DIRECT_PROPERTY_DESCRIPTION_KEY = 'description';
export const DIRECT_PROPERTY_PROGRESS_KEY = 'progressMeasurement';
export const DIRECT_PROPERTY_CYCLE_KEY = 'goalCycle';
export const DIRECT_PROPERTY_PARENT_KEY = 'parents';
export const DIRECT_PROPERTY_LAST_GOAL_ACTIVITY_KEY = 'lastGoalActivity';

// quick filter types
export const QUICK_FILTER_TYPE_SCOPE_ITEM = 'scopeItem';
export const QUICK_FILTER_TYPE_PROPERTY = 'property';

// slots
export const SLOTS = {
  DATA_LOADING: 'data_loading',
  LOCKED: 'locked',
  CYCLE_SELECTOR: 'cycle_selector',
  STRING_FILTER: 'string_filter',
  FILTER: 'filter',
  FILTER_IN_MENU: 'filter_in_menu',
  SORTER_IN_MENU: 'sorter_in_menu',
  PROPERTIES: 'properties',
  GROUPING: 'grouping',
  SORTER: 'sorter',
  COMPARE_TO: 'compare_to_previous',
  PROGRESS_DISPLAY_OPTION: 'progress_display_option',
  BOUNDED_PROGRESS_OPTION: 'bounded_progress_option',
  LAYOUT: 'layout',
  EXPAND_BUTTONS: 'expand_buttons',
  CREATE: 'create',
  VIEW_TITLE_EDITOR: 'view_title_editor',
};

export const SLOTS_LIST = Object.values(SLOTS);

// icons & emojis recent section
export const RECENT_ICONS_LOCALSTORAGE_PREFIX = 'recent_icons';
export const RECENT_EMOJIS_LOCALSTORAGE_PREFIX = 'recent_emojis';

// Datadog
export const CASCADE_TABLE_ITEM_DRAGGED = 'Cascade table item dragged';
export const CASCADE_TABLE_HEADER_WIDTH_CHANGED = 'Cascade table header width changed';
export const BLOCKED_BY_FEATURE_GATE = 'Blocked by Feature Gate';
export const TREE_VIEW_TOUCH_DEVICE_DETECTED = 'Tree view touch device detected';

export const DATADOG_ACTION_WHITELIST = {
  [CASCADE_TABLE_ITEM_DRAGGED]: null,
  [CASCADE_TABLE_HEADER_WIDTH_CHANGED]: null,
  [BLOCKED_BY_FEATURE_GATE]: null,
  [TREE_VIEW_TOUCH_DEVICE_DETECTED]: null,
};

// ACCESS POLICY Extra Types
export const ACCESS_POLICY_TYPE_REMOVE = 'remove';
export const ACCESS_POLICY_TYPE_MIXED = 'mixed';

// USER Status
export const USER_STATUS_NO_INVITATION = 'no_invitation';
export const USER_STATUS_INVITE_PENDING = 'invite_pending';
export const USER_STATUS_ACTIVE = 'active';
export const USER_STATUS_ARCHIVED = 'archived';
export const USER_STATUS_DEMO = 'demo';

// SPACE Status
export const SPACE_STATUS_ARCHIVED = 'archived';
export const SPACE_STATUS_ACTIVE = 'active';
export const SPACE_STATUS_JOINED = 'joined';
export const SPACE_STATUS_ALL = 'all';

// Resource Center
export const RESOURCE_CENTER_OVERVIEW = 'overview';
export const RESOURCE_CENTER_ONBOARDING_GUIDE = 'onboarding-guide';
export const RESOURCE_CENTER_OKR_RESOURCES = 'okr-resources';
export const RESOURCE_CENTER_VIDEOS = 'videos';

export const APP_MODES = {
  requiresMajorUpdate: 'requires major update',
  requiresMinorUpdate: 'requires minor update',
  foreignLogin: 'foreign login',
  login: 'login',
  stepUp: 'step up',
  app: 'app',
  maintenance: 'maintenance',
  loading: 'loading',
};

// collection of observable events: <namespace>.<eventName>
export const EVENTS = {
  SESSION: {
    LOGIN: 'SESSION.LOGIN',
    LOGOUT: 'SESSION.LOGOUT',
    SWITCH_ACCOUNT: 'SESSION.SWITCH_ACCOUNT',
    IDENTIFY: 'SESSION.IDENTIFY',
    LOGIN_PUBLISHED_VIEW: 'SESSION.LOGIN_PUBLISHED_VIEW',
  },
  NAVIGATION: {
    FAVORITE_CREATED: 'NAVIGATION.FAVORITE_CREATED',
    FAVORITE_DELETED: 'NAVIGATION.FAVORITE_DELETED',
    PIN_CREATED: 'NAVIGATION.PIN_CREATED',
    PIN_DELETED: 'NAVIGATION.PIN_DELETED',
  },
  USER: {
    USERS_CREATED: 'USER.USERS_CREATED',
    USERS_DELETED: 'USER.USERS_DELETED',
    USERS_INVITED: 'USER.USERS_INVITED',
    ACTIVATED: 'USER.ACTIVATED',
  },
  GOAL: {
    GOALS_CREATED: 'GOAL.GOALS_CREATED',
    GOALS_DELETED: 'GOAL.GOALS_DELETED',
    GOAL_ACTIVITIES_CREATED: 'GOAL.GOAL_ACTIVITIES_CREATED',
  },
  PLANNING: {
    PLANNING_CREATED: 'PLANNING.PLANNING_CREATED',
    GOALS_CREATED: 'PLANNING.GOALS_CREATED',
    GOALS_DELETED: 'PLANNING.GOALS_DELETED',
    GOALS_PUBLISHED: 'PLANNING.GOALS_PUBLISHED',
  },
  CHECKIN: {
    UPDATE_CREATED: 'CHECKIN.UPDATE_CREATED',
    UPDATE_DELETED: 'CHECKIN.UPDATE_DELETED',
    UPDATE_TEMPLATE_CREATED: 'CHECKIN.UPDATE_TEMPLATE_CREATED',
    UPDATE_TEMPLATE_DELETED: 'CHECKIN.UPDATE_TEMPLATE_DELETED',
    SCHEDULE_CREATED: 'CHECKIN.SCHEDULE_CREATED',
    SCHEDULE_DELETED: 'CHECKIN.SCHEDULE_DELETED',
  },
  VIEW: {
    VIEW_CREATED: 'VIEW.VIEW_CREATED',
    VIEW_DELETED: 'VIEW.VIEW_DELETED',
    ATLAS_VIEW_VISITED: 'VIEW.ATLAS_VIEW_VISITED',
    VIEW_PUBLISHED: 'VIEW.VIEW_PUBLISHED',
    VIEW_UNPUBLISHED: 'VIEW.VIEW_UNPUBLISHED',
    PUBLISHED_VIEW_VISITED: 'VIEW.PUBLISHED_VIEW_VISITED',
    GOAL_TREE_VIEW_VISITED: 'VIEW.GOAL_TREE_VIEW_VISITED',
  },
  PROPERTY: {
    GOAL_CYCLE_CREATED: 'PROPERTY.GOAL_CYCLE_CREATED',
    GOAL_CYCLE_DELETED: 'PROPERTY.GOAL_CYCLE_DELETED',
  },
  GRID_PAGE: {
    GRID_PAGES_CREATED: 'GRID_PAGE.GRID_PAGES_CREATED',
    GRID_PAGES_DELETED: 'GRID_PAGE.GRID_PAGES_DELETED',
    GRID_PAGE_TILES_CREATED: 'GRID_PAGE.GRID_PAGE_TILES_CREATED',
    GRID_PAGE_TILES_DELETED: 'GRID_PAGE.GRID_PAGE_TILES_DELETED',
  },
  INSIGHTS: {
    DASHBOARD_VISITED: 'DASHBOARD.DASHBOARD_VISITED',
    CUSTOM_DASHBOARD_VISITED: 'DASHBOARD.CUSTOM_DASHBOARD_VISITED',
  },
  INTERACTION: {
    REACTION_CREATED: 'INTERACTION.REACTION_CREATED',
    COMMENT_CREATED: 'INTERACTION.COMMENT_CREATED',
  },
  RESOURCE_CENTER: { RESOURCE_CENTER_VIEWED: 'RESOURCE_CENTER.RESOURCE_CENTER_VIEWED' },
  UPSELL: {
    FEATURE_GATE_VIEWED: 'UPSELL.FEATURE_GATE_VIEWED',
    CONTACT_SALES_VIEWED: 'UPSELL.CONTACT_SALES_VIEWED',
    CONTACT_SUPPORT_VIEWED: 'UPSELL.CONTACT_SUPPORT_VIEWED',
  },
  INTEGRATION: {
    INTEGRATION_ENABLED: 'INTEGRATION.INTEGRATION_ENABLED',
    INTEGRATION_DISABLED: 'INTEGRATION.INTEGRATION_DISABLED',
    DATASOURCE_CREATED: 'INTEGRATION.DATASOURCE_CREATED',
    DATASOURCE_DELETED: 'INTEGRATION.DATASOURCE_DELETED',
  },
  SETTINGS: { PROFILE_SETTINGS_VISITED: 'SETTINGS.PROFILE_SETTINGS_VISITED' },
  SIDEBAR: { MINIMAL_CONTENT_WIDTH_REACHED: 'SIDEBAR.MINIMAL_CONTENT_WIDTH_REACHED' },
};

export const CARD_SIZE_SMALL = 'small';
export const CARD_SIZE_MEDIUM = 'medium';
export const CARD_SIZE_LARGE = 'large';

export const MAX_AUTO_LOAD_LIMIT = 100;

export const CHART_CATEGORY_LIMIT = 200;
export const CHART_SERIES_LIMIT = 50;

export const DEFAULT_SIDEBAR_WIDTH = 240;
export const MIN_SIDEBAR_WIDTH = 240;
export const MAX_SIDEBAR_WIDTH = 460;

export const MAX_WIDTH_SIDE_PEEK = 0.66;
export const MIN_WIDTH_SIDE_PEEK = 560;

export const MIN_WIDTH_AI_CHAT_PANEL = 360;
export const MAX_WIDTH_RATIO_AI_CHAT_PANEL = 0.5;
export const DEFAULT_WIDTH_AI_CHAT_PANEL = 420;

// boxed-small content (760px) + layout-detail horizontal padding (126px * 2)
export const DETAIL_CONTENT_MIN_WIDTH = 1012;

export const legacyRoutePrefix = 'legacy-';

export const ROUTES_WITHOUT_SIDEBAR = [
  routeName.teamsTeamOkrs,
  routeName.teamsAccountOkrs,
  routeName.publishedView,
];

// Predefined PDF report widths
export const PRINTABLE_WIDTH_NARROW = 794; // A4 paper width (210mm at 96 DPI)
export const PRINTABLE_WIDTH_STANDARD = 1200; // Standard desktop dashboard width
export const PRINTABLE_WIDTH_WIDE = 1600; // Wide desktop / multi-column dashboards
export const PRINTABLE_WIDTH_EXTRA_WIDE = 1920; // Full HD width for dense layouts

// Microsoft Teams connect flow: browser query params echoed back to MyNotifications
export const msTeamsConnectError = { notEnabled: 'not_enabled' };

export const msTeamsConnectQuery = {
  error: 'ms_teams_connect_error',
  success: 'ms_teams_connect_success',
};
